import { css, cx } from '@linaria/atomic';
import type { SizeProps } from '../avatar/avatar.styles';

const groupContainerBaseStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const containerDimensionStyles: Record<SizeProps, string> = {
  xxs: css`
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
  `,
  xs: css`
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
  `,
  small: css`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
  `,
  medium: css`
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  `,
  large: css`
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  `,
  xl: css`
    width: 64px;
    height: 64px;
    min-width: 64px;
    min-height: 64px;
  `,
  xxl: css`
    width: 80px;
    height: 80px;
    min-width: 80px;
    min-height: 80px;
  `,
};

export const groupContainerStyles = (size: SizeProps) => cx(groupContainerBaseStyles, containerDimensionStyles[size]);

const avatarGroupFontSizeStyles: Record<SizeProps, string> = {
  xxs: css`
    font-size: var(--fabric-font-size-xs);
  `,
  xs: css`
    font-size: var(--fabric-font-size-xs);
  `,
  small: css`
    font-size: var(--fabric-font-size-xs);
  `,
  medium: css`
    font-size: var(--fabric-font-size-sm);
  `,
  large: css`
    font-size: var(--fabric-font-size-md);
  `,
  xl: css`
    font-size: var(--fabric-font-size-h3);
  `,
  xxl: css`
    font-size: var(--fabric-font-size-h3);
  `,
};

const itemBaseStyles = {
  interactive: css`
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    transition: transform 0.2s ease-in-out;
    outline: 2px solid var(--fabric-color-neutral-00);
    border-radius: 50%;

    &:hover {
      transform: scale(1.15);
      cursor: pointer;
    }
  `,
  default: css`
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    transition: transform 0.2s ease-in-out;
    outline: 2px solid var(--fabric-color-neutral-00);
    border-radius: 50%;

    &:hover {
      transform: scale(1.15);
      z-index: 10;
      cursor: default;
    }
  `,
} as const;

const itemWrapperBaseStyles = css`
  &:hover {
    z-index: 10;
  }
`;

const itemWrapperStylesByCount: Record<2 | 3 | 4, string[]> = {
  2: [
    css`
      position: absolute;
      width: 65%;
      height: 65%;
      min-width: 65%;
      min-height: 65%;
      top: 7.5%;
      left: 0;
      z-index: 1;
    `,
    css`
      position: absolute;
      width: 65%;
      height: 65%;
      min-width: 65%;
      min-height: 65%;
      bottom: 7.5%;
      right: 0;
      z-index: 2;
    `,
  ],
  3: [
    css`
      position: absolute;
      width: 55%;
      height: 55%;
      min-width: 55%;
      min-height: 55%;
      top: 0;
      left: 22.5%;
      z-index: 2;
    `,
    css`
      position: absolute;
      width: 55%;
      height: 55%;
      min-width: 55%;
      min-height: 55%;
      bottom: 25%;
      left: -5%;
      z-index: 1;
    `,
    css`
      position: absolute;
      width: 55%;
      height: 55%;
      min-width: 55%;
      min-height: 55%;
      bottom: 25%;
      right: -5%;
      z-index: 3;
    `,
  ],
  4: [
    css`
      position: absolute;
      width: 52%;
      height: 52%;
      min-width: 52%;
      min-height: 52%;
      top: 0;
      left: 15%;
      z-index: 2;
    `,
    css`
      position: absolute;
      width: 52%;
      height: 52%;
      min-width: 52%;
      min-height: 52%;
      top: 0;
      right: 0;
      z-index: 4;
    `,
    css`
      position: absolute;
      width: 52%;
      height: 52%;
      min-width: 52%;
      min-height: 52%;
      bottom: 10%;
      left: 0;
      z-index: 1;
    `,
    css`
      position: absolute;
      width: 52%;
      height: 52%;
      min-width: 52%;
      min-height: 52%;
      bottom: 10%;
      right: 15%;
      z-index: 3;
    `,
  ],
};

export const itemWrapperStyles = (count: number, index: number) => {
  if (count < 2 || count > 4) return undefined;

  return cx(itemWrapperBaseStyles, itemWrapperStylesByCount[count as 2 | 3 | 4][index]);
};

export const itemAvatarStyles = (size: SizeProps, isInteractive = false) => {
  return cx(isInteractive ? itemBaseStyles.interactive : itemBaseStyles.default, avatarGroupFontSizeStyles[size]);
};

const overflowAvatarBaseStyles = {
  interactive: css`
    background-color: var(--fabric-color-neutral-30);
    color: var(--fabric-color-neutral-00);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    border: none;
    font-weight: var(--fabric-font-weight-bold);
    line-height: 1;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--fabric-color-focus-ring);
      outline-offset: 2px;
    }
  `,
  default: css`
    background-color: var(--fabric-color-neutral-30);
    color: var(--fabric-color-neutral-00);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    border: none;
    font-weight: var(--fabric-font-weight-bold);
    line-height: 1;
    cursor: default;
  `,
} as const;

export const overflowAvatarStyles = (size: SizeProps, isInteractive: boolean) => {
  return cx(
    isInteractive ? overflowAvatarBaseStyles.interactive : overflowAvatarBaseStyles.default,
    avatarGroupFontSizeStyles[size]
  );
};
